import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { useGetAssociatedUsers } from "../queries/usersRoles";
import { useSyncEmployeeManagers } from "../queries/managerAssignment";
import AlertDialog from "../components/shared/AlertDialog";
import InfoBox from "../components/shared/InfoBox";
import { Label } from "../components/shared/InputField";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../components/utils";
import { Employee } from "../types/Employee";

type AssignToManagerDialogProps = {
  selectedEmployees: Employee[];
  onClose: () => void;
};

type ChipStatus = "pristine" | "toAdd" | "toRemove";

function sortedEquals(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

export const AssignToManagerDialog = (props: AssignToManagerDialogProps) => {
  const { selectedEmployees, onClose } = props;
  const [managerIds, setManagerIds] = useState<string[]>([]);
  const [employees, setEmployees] = useState<Employee[]>(selectedEmployees);

  const [originalByEmployee, setOriginalByEmployee] = useState<
    Map<string, string[]>
  >(
    () =>
      new Map(
        selectedEmployees.map((emp) => [emp.id, [...(emp.managers || [])]])
      )
  );
  const [stagedByEmployee, setStagedByEmployee] = useState<
    Map<string, string[]>
  >(
    () =>
      new Map(
        selectedEmployees.map((emp) => [emp.id, [...(emp.managers || [])]])
      )
  );

  const [pendingRemoval, setPendingRemoval] = useState<{
    employeeId: string;
    managerId: string;
  } | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  const { data: users, isLoading: usersLoading } = useGetAssociatedUsers();
  const { mutate: syncManagers, isLoading: isSaving } =
    useSyncEmployeeManagers();

  const managers = useMemo(() => {
    if (!users) return [];
    return users.filter((user) => user.role?.name === "MANAGER");
  }, [users]);

  const userIdToName = useMemo(() => {
    const map = new Map<string, string>();
    managers.forEach((m) => {
      if (m.userId != null) map.set(m.userId.toString(), m.name);
    });
    return map;
  }, [managers]);

  const changedEmployeeIds = useMemo(
    () =>
      employees
        .map((emp) => emp.id)
        .filter(
          (id) =>
            !sortedEquals(
              originalByEmployee.get(id) || [],
              stagedByEmployee.get(id) || []
            )
        ),
    [employees, originalByEmployee, stagedByEmployee]
  );

  const hasUnsavedChanges = changedEmployeeIds.length > 0;

  const toggleManager = (id: string) => {
    setManagerIds((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  const handleRemoveEmployeeFromList = (id: string) => {
    setEmployees((prev) => prev.filter((emp) => emp.id !== id));
  };

  const handleStageManagerRemoval = (employeeId: string, managerId: string) => {
    setPendingRemoval({ employeeId, managerId });
  };

  const confirmStagedRemoval = () => {
    if (!pendingRemoval) return;
    const { employeeId, managerId } = pendingRemoval;
    setStagedByEmployee((prev) => {
      const next = new Map(prev);
      next.set(
        employeeId,
        (next.get(employeeId) || []).filter((m) => m !== managerId)
      );
      return next;
    });
    setPendingRemoval(null);
  };

  const handleUndoRemoval = (employeeId: string, managerId: string) => {
    setStagedByEmployee((prev) => {
      const next = new Map(prev);
      const current = next.get(employeeId) || [];
      if (!current.includes(managerId)) {
        next.set(employeeId, [...current, managerId]);
      }
      return next;
    });
  };

  const handleUnstageAddition = (employeeId: string, managerId: string) => {
    setStagedByEmployee((prev) => {
      const next = new Map(prev);
      next.set(
        employeeId,
        (next.get(employeeId) || []).filter((m) => m !== managerId)
      );
      return next;
    });
  };

  const handleStageAdd = () => {
    if (managerIds.length === 0) {
      toast.error("Please select at least one manager to add.");
      return;
    }
    if (employees.length === 0) {
      toast.error("Please select at least one employee.");
      return;
    }

    setStagedByEmployee((prev) => {
      const next = new Map(prev);
      employees.forEach((emp) => {
        const current = next.get(emp.id) || [];
        next.set(emp.id, Array.from(new Set([...current, ...managerIds])));
      });
      return next;
    });
    setManagerIds([]);
  };

  const handleDiscardChanges = () => {
    setStagedByEmployee(new Map(originalByEmployee));
  };

  const handleSave = () => {
    const updates = changedEmployeeIds.map((employeeId) => ({
      employeeId,
      managerIds: stagedByEmployee.get(employeeId) || [],
    }));

    syncManagers(
      { updates },
      {
        onSuccess: () => {
          setOriginalByEmployee(new Map(stagedByEmployee));
          toast.success("Managers updated successfully!", { autoClose: 2000 });
        },
        onError: (err: any) => {
          const errMsg =
            err?.response?.data?.reason ||
            err?.message ||
            "Failed to update managers.";
          toast.error(errMsg, { autoClose: 3000 });
        },
      }
    );
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setConfirmClose(true);
      return;
    }
    onClose();
  };

  return (
    <>
      <Box className="px-10 text-lg font-medium border-b py-7">
        <h6 className="flex justify-between items-center">
          <span>Assign Managers</span>
          <span
            onClick={handleClose}
            className="cursor-pointer text-gray-400 hover:text-gray-600"
          >
            <Icon icon="ic:round-close" width={24} height={24} />
          </span>
        </h6>
      </Box>

      <VStack className="w-full px-10 py-7 gap-6">
        {hasUnsavedChanges ? (
          <InfoBox
            message={`You have ${changedEmployeeIds.length} employee${
              changedEmployeeIds.length === 1 ? "" : "s"
            } with unsaved manager changes. Click Save Changes to apply or Discard Changes to revert.`}
          />
        ) : (
          <InfoBox message='Adding or removing managers below only takes effect once you click "Save Changes".' />
        )}

        <VStack className="gap-2">
          <Label className="text-sm font-medium text-gray-700">
            Add Manager(s)
          </Label>
          <span className="text-xs text-gray-500">
            Selected managers will be added to every employee below, alongside
            any managers they already have.
          </span>
          {usersLoading ? (
            <span className="text-xs text-gray-400">Loading managers...</span>
          ) : managers.length === 0 ? (
            <span className="text-xs text-amber-600">
              No users with the Manager role found. Assign the Manager role in
              Users &amp; Roles first.
            </span>
          ) : (
            <Box className="border border-gray-300 rounded-md max-h-40 overflow-y-auto divide-y divide-gray-100">
              {managers.map((m) => {
                const id = m.userId?.toString() ?? "";
                return (
                  <label
                    key={id}
                    className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={managerIds.includes(id)}
                      onChange={() => toggleManager(id)}
                    />
                    <span>{m.name}</span>
                  </label>
                );
              })}
            </Box>
          )}
          <HStack className="justify-end">
            <ButtonPrimary1
              onClick={handleStageAdd}
              disabled={employees.length === 0 || managerIds.length === 0}
            >
              Add to Selected Employees
            </ButtonPrimary1>
          </HStack>
        </VStack>

        <VStack className="gap-2">
          <Label className="text-sm font-medium text-gray-700">
            Selected Employees ({employees.length})
          </Label>
          <span className="text-xs text-gray-500">
            Click the &times; next to a manager&apos;s name to remove them from
            that employee.
          </span>

          {employees.length === 0 ? (
            <Box className="py-6 text-center text-gray-400 text-sm border border-gray-200 rounded-md bg-gray-50">
              No employees selected.
            </Box>
          ) : (
            <Box className="border border-gray-200 rounded-md overflow-hidden">
              <table className="w-full text-sm text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-2.5 font-semibold text-gray-600">
                      Employee
                    </th>
                    <th className="px-4 py-2.5 font-semibold text-gray-600">
                      Department
                    </th>
                    <th className="px-4 py-2.5 font-semibold text-gray-600">
                      Current Managers
                    </th>
                    <th className="px-4 py-2.5 w-10" />
                  </tr>
                </thead>
                <tbody className="max-h-52 overflow-y-auto divide-y divide-gray-100">
                  {employees.map((emp) => {
                    const original = originalByEmployee.get(emp.id) || [];
                    const staged = stagedByEmployee.get(emp.id) || [];
                    const displayManagerIds = Array.from(
                      new Set([...original, ...staged])
                    );

                    return (
                      <tr key={emp.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5">
                          <span className="font-medium text-gray-900">
                            {emp.employeeName}
                          </span>
                          {emp.employeeDesignation && (
                            <span className="block text-xs text-gray-500">
                              {emp.employeeDesignation}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {emp.department || "—"}
                        </td>
                        <td className="px-4 py-2.5">
                          {displayManagerIds.length > 0 ? (
                            <HStack className="flex-wrap gap-1.5">
                              {displayManagerIds.map((managerId) => {
                                const key = `${emp.id}:${managerId}`;
                                const inOriginal = original.includes(managerId);
                                const inStaged = staged.includes(managerId);
                                const status: ChipStatus = !inOriginal
                                  ? "toAdd"
                                  : !inStaged
                                  ? "toRemove"
                                  : "pristine";
                                const name =
                                  userIdToName.get(managerId) ?? managerId;

                                const chipClass =
                                  status === "toAdd"
                                    ? "bg-green-50 text-green-700"
                                    : status === "toRemove"
                                    ? "bg-red-50 text-red-400 line-through"
                                    : "bg-gray-100 text-gray-700";

                                return (
                                  <span
                                    key={key}
                                    className={`inline-flex items-center gap-1 rounded-full pl-2.5 pr-1.5 py-0.5 text-xs ${chipClass}`}
                                  >
                                    {name}
                                    {status === "toRemove" ? (
                                      <button
                                        onClick={() =>
                                          handleUndoRemoval(emp.id, managerId)
                                        }
                                        className="text-gray-400 hover:text-primary transition-colors"
                                        title={`Undo removal of ${name}`}
                                      >
                                        <Icon
                                          icon="lucide:undo-2"
                                          width={12}
                                          height={12}
                                        />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() =>
                                          status === "toAdd"
                                            ? handleUnstageAddition(
                                                emp.id,
                                                managerId
                                              )
                                            : handleStageManagerRemoval(
                                                emp.id,
                                                managerId
                                              )
                                        }
                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                        title={`Remove ${name}`}
                                      >
                                        <Icon
                                          icon="ic:round-close"
                                          width={12}
                                          height={12}
                                        />
                                      </button>
                                    )}
                                  </span>
                                );
                              })}
                            </HStack>
                          ) : (
                            <span className="text-gray-400 italic text-xs">
                              Unassigned
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <button
                            onClick={() => handleRemoveEmployeeFromList(emp.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            title="Remove employee from this list"
                          >
                            <Icon
                              icon="ic:round-close"
                              width={16}
                              height={16}
                            />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          )}
        </VStack>
      </VStack>

      <Box className="px-10 py-7 border-t">
        <HStack className="justify-between gap-3">
          <HStack className="gap-3">
            <ButtonPrimary1 onClick={handleClose}>Close</ButtonPrimary1>
            {hasUnsavedChanges && (
              <ButtonPrimary1 onClick={handleDiscardChanges}>
                Discard Changes
              </ButtonPrimary1>
            )}
          </HStack>
          <ButtonPrimary
            onClick={handleSave}
            loading={isSaving}
            disabled={!hasUnsavedChanges || isSaving}
          >
            Save Changes
          </ButtonPrimary>
        </HStack>
      </Box>

      <AlertDialog
        open={!!pendingRemoval}
        message="This manager will be removed from this employee when you save changes."
        primaryActionText="Remove"
        secondaryActionText="Cancel"
        onPrimaryAction={confirmStagedRemoval}
        onSecondaryAction={() => setPendingRemoval(null)}
        onClose={() => setPendingRemoval(null)}
      />

      <AlertDialog
        open={confirmClose}
        message="You have unsaved manager changes. Are you sure you want to close without saving?"
        primaryActionText="Discard & Close"
        secondaryActionText="Keep Editing"
        onPrimaryAction={() => {
          setConfirmClose(false);
          onClose();
        }}
        onSecondaryAction={() => setConfirmClose(false)}
        onClose={() => setConfirmClose(false)}
      />
    </>
  );
};
